import { UserButton, useAuth } from '@clerk/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import { getSportPack, DEFAULT_SPORT_PACK_ID } from './sport-packs/registry.ts';
import { HttpBoardStore, HttpFormationStore, HttpPlayStore, HttpTutorialStore, WebFileExporter, WebPlayRecorder, canvasToPngBlob } from './adapters/index.ts';
import { Gate } from './ui/Gate.tsx';
import { Board } from './ui/Board.tsx';
import { Toolbar } from './ui/Toolbar.tsx';
import { ViewPicker } from './ui/ViewPicker.tsx';
import { TimelineBar } from './ui/TimelineBar.tsx';
import { RecordPanel } from './ui/RecordPanel.tsx';
import { SavedPlays } from './ui/SavedPlays.tsx';
import { PhoneWorkspace } from './ui/phone/PhoneWorkspace.tsx';
import { useLayout } from './ui/layout.ts';
import { useRecording } from './ui/useRecording.ts';
import { useBoard, type Tool } from './ui/useBoard.ts';
import { useFormations } from './ui/useFormations.ts';
import { useSavedPlays } from './ui/useSavedPlays.ts';
import type { BoardFrame } from './ui/recordingFrame.ts';
import { TutorialOverlay } from './ui/tutorial/TutorialOverlay.tsx';
import { useTutorial } from './ui/tutorial/useTutorial.ts';

const SHORTCUT_TOOLS: Readonly<Record<string, Tool>> = {
  v: 'select',
  a: 'move',
  p: 'pass',
  s: 'shot',
  f: 'freehand',
  e: 'erase',
};

function isTextField(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable;
}

/**
 * Whether this visit asked for the developer tools (`?dev=1`). Read once, never
 * stored: a coach who follows such a link is back to the plain board on their
 * next visit.
 */
function developerRequested(): boolean {
  return new URLSearchParams(window.location.search).has('dev');
}

function fileStem(name: string): string {
  return name.trim().replace(/[^\w\d-]+/g, '-').replace(/-+/g, '-').toLowerCase() || 'play';
}

/**
 * What videos and exports are called: the saved play on the board, or for a
 * board no saved play is behind, the day — "Play 2026-10-11" (#92).
 */
function fileName(playName: string | undefined, today: Date): string {
  if (playName) return playName;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `Play ${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
}

function Workspace(): JSX.Element {
  const pack = getSportPack(DEFAULT_SPORT_PACK_ID);
  const { getToken } = useAuth();
  // Clerk may hand out a new `getToken` on any render; the store must not
  // follow it, or `useBoard` would reload (and reset) the board each time.
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const storeRef = useRef<HttpBoardStore | null>(null);
  storeRef.current ??= new HttpBoardStore(() => getTokenRef.current());
  const api = useBoard(pack, storeRef.current);
  const formationStoreRef = useRef<HttpFormationStore | null>(null);
  formationStoreRef.current ??= new HttpFormationStore(() => getTokenRef.current());
  const formations = useFormations(formationStoreRef.current, api);
  const playStoreRef = useRef<HttpPlayStore | null>(null);
  playStoreRef.current ??= new HttpPlayStore(() => getTokenRef.current());
  const plays = useSavedPlays(playStoreRef.current, api, !api.loading && !api.loadError);
  // Read when a file is made, so a board left open overnight gets today's date.
  const playName = plays.current?.name;
  const currentFileName = useCallback(() => fileName(playName, new Date()), [playName]);
  const tutorialStoreRef = useRef<HttpTutorialStore | null>(null);
  tutorialStoreRef.current ??= new HttpTutorialStore(() => getTokenRef.current());
  // While a recording runs, the board logs every frame it draws into the
  // recorder; the video is made from that log after the coach stops.
  const recorder = useMemo(() => new WebPlayRecorder<BoardFrame>(), []);
  const [recording, setRecording] = useState(false);
  const logFrame = useCallback((frame: BoardFrame) => recorder.logFrame(frame), [recorder]);
  // One recording, whichever layout shows its controls.
  const recordingApi = useRecording(recorder, currentFileName, setRecording);
  const layout = useLayout();
  const phone = layout !== 'desktop';
  const tutorial = useTutorial(tutorialStoreRef.current, !api.loading && !api.loadError);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const exporterRef = useRef(new WebFileExporter());
  const [importError, setImportError] = useState<string | null>(null);
  // Fixed for the life of the page: the flag comes from the URL it was opened with.
  const developer = useRef(developerRequested()).current;

  /* ---------------- keyboard shortcuts (desktop is the design centre) -------- */

  // Which way a held Space is playing, or null when it is not held.
  const holdingRef = useRef<1 | -1 | null>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (isTextField(event.target)) return;
      const key = event.key.toLowerCase();
      const modifier = event.metaKey || event.ctrlKey;

      if (modifier && key === 'z') {
        event.preventDefault();
        if (event.shiftKey) api.redoAction();
        else api.undoAction();
        return;
      }
      if (modifier && key === 'y') {
        event.preventDefault();
        api.redoAction();
        return;
      }
      if (modifier) return;

      const tool = SHORTCUT_TOOLS[key];
      if (tool) {
        event.preventDefault();
        // The toolbar greys these out while a group is selected; the keys agree.
        if (api.multiSelection && tool !== 'select') return;
        api.setTool(tool);
        return;
      }

      switch (event.key) {
        case ' ': {
          event.preventDefault();
          // Space moves the play on, Shift+Space back. A tap steps one phase
          // (quick taps shuffle); once the key repeats it is being held, and
          // the play runs on smoothly until it is let go.
          const direction = event.shiftKey ? -1 : 1;
          if (!event.repeat) api.tapPhase(direction);
          else if (holdingRef.current === null) {
            holdingRef.current = direction;
            api.holdPhase(direction);
          }
          break;
        }
        case 'k':
        case 'K':
          event.preventDefault();
          api.addPhase();
          break;
        case 'h':
        case 'H':
          event.preventDefault();
          api.cycleView();
          break;
        case 'Backspace':
        case 'Delete':
          event.preventDefault();
          api.deleteSelected();
          break;
        case 'Escape':
          api.setSelectedIds([]);
          api.setTool('select');
          break;
        default:
          break;
      }
    };

    // Letting go of a held Space ends the hold: the move under way finishes.
    const letGo = (): void => {
      if (holdingRef.current === null) return;
      holdingRef.current = null;
      api.releasePhase();
    };
    const onKeyUp = (event: KeyboardEvent): void => {
      if (event.key === ' ') letGo();
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    // Switching away with the key down never delivers its keyup.
    window.addEventListener('blur', letGo);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', letGo);
    };
  }, [api]);

  /* ---------------- export / import ---------------- */

  const exportPng = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const blob = await canvasToPngBlob(canvas);
    await exporterRef.current.saveBlob(blob, `${fileStem(currentFileName())}.png`);
  }, [currentFileName]);

  const exportJson = useCallback(async () => {
    const blob = new Blob([api.toJson()], { type: 'application/json' });
    await exporterRef.current.saveBlob(blob, `${fileStem(currentFileName())}.play.json`);
  }, [api, currentFileName]);

  const importJson = useCallback(async () => {
    setImportError(null);
    const picked = await exporterRef.current.openText('application/json,.json');
    if (!picked) return;
    try {
      api.loadFromJson(picked.text);
    } catch (cause) {
      setImportError((cause as Error).message);
    }
  }, [api]);

  return (
    <div className={phone ? `app app--phone app--${layout}` : 'app'}>
      <header className="topbar">
        <div className="topbar__brand">
          <span className="topbar__mark" aria-hidden="true">
            ◎
          </span>
          <div>
            <h1 className="topbar__title">Coach Gameboard</h1>
            <p className="topbar__sub">
              {pack.name} · {api.doc.entities.length} objects · {api.doc.keyframes.length} phases
            </p>
          </div>
        </div>
        {importError && <p className="topbar__error">{importError}</p>}
        <div className="topbar__account">
          {!api.loading && !api.loadError && <ViewPicker view={api.view} onChange={api.setView} />}
          {api.saveState === 'failed' && (
            <span className="topbar__save topbar__save--failed" title="Not saved — retrying">
              Not saved — retrying
            </span>
          )}
          {api.saveState === 'saving' && (
            <span className="topbar__save" title="Saving…">
              Saving…
            </span>
          )}
          {/* The tutorial lives in the coach's own account menu, not on the board. */}
          <UserButton>
            <UserButton.MenuItems>
              <UserButton.Action
                label="Show tutorial"
                labelIcon={<span aria-hidden="true">?</span>}
                onClick={tutorial.replay}
              />
            </UserButton.MenuItems>
          </UserButton>
        </div>
      </header>

      {api.loadError ? (
        <p className="loading loading--error">
          Could not load your board: {api.loadError}{' '}
          <button type="button" className="btn" onClick={() => window.location.reload()}>
            Retry
          </button>
        </p>
      ) : api.loading ? (
        <p className="loading">Loading your board…</p>
      ) : phone ? (
        <PhoneWorkspace
          api={api}
          formations={formations}
          plays={plays}
          recording={recordingApi}
          tutorial={tutorial}
          developer={developer}
          canvasRef={canvasRef}
          onFrame={recording ? logFrame : undefined}
          onExportPng={() => void exportPng()}
          onExportJson={() => void exportJson()}
          onImportJson={() => void importJson()}
        />
      ) : (
        <main className="layout">
          <Toolbar
            api={api}
            formations={formations}
            developer={developer}
            onExportPng={() => void exportPng()}
            onExportJson={() => void exportJson()}
            onImportJson={() => void importJson()}
          />
          <Board api={api} canvasRef={canvasRef} onFrame={recording ? logFrame : undefined} />
          <RecordPanel recording={recordingApi}>
            <SavedPlays plays={plays} />
          </RecordPanel>
        </main>
      )}

      {!phone && <TimelineBar api={api} />}
      <TutorialOverlay tutorial={tutorial} phone={phone} />
    </div>
  );
}

export function App(): JSX.Element {
  return (
    <Gate>
      <Workspace />
    </Gate>
  );
}
